<script setup lang="ts">
import { ref, nextTick, onMounted, onUnmounted, watch } from 'vue'
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon
} from '@heroicons/vue/24/outline'
import { renderMarkdown } from '@/utils/markdown'
import { aiService } from '@/services/ai'
import type { ChatMessage } from '@/types'

const modalRef = ref<HTMLElement | null>(null)

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const trapFocus = (e: KeyboardEvent) => {
  if (e.key !== 'Tab' || !modalRef.value) return
  const focusable = Array.from(modalRef.value.querySelectorAll<HTMLElement>(FOCUSABLE))
  if (focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault()
      last.focus()
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
}

// Reactive state
const isOpen = ref(false)
const isLoading = ref(false)
const userInput = ref('')
const messages = ref<ChatMessage[]>([])
const messagesContainer = ref<HTMLElement | null>(null)
let previouslyFocused: HTMLElement | null = null

// Auto-scroll to bottom when messages change
const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

watch(messages, scrollToBottom, { deep: true })
watch(isLoading, scrollToBottom)

// Methods
const openChat = async () => {
  previouslyFocused = document.activeElement as HTMLElement
  isOpen.value = true
  document.addEventListener('keydown', trapFocus)
  await nextTick()
  const firstFocusable = modalRef.value?.querySelector<HTMLElement>(FOCUSABLE)
  firstFocusable?.focus()
}

const closeChat = () => {
  isOpen.value = false
  document.removeEventListener('keydown', trapFocus)
  previouslyFocused?.focus()
}

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOpen.value) {
    closeChat()
  }
}

onMounted(() => document.addEventListener('keydown', handleKeydown))
onUnmounted(() => document.removeEventListener('keydown', handleKeydown))

const suggestedQuestions = [
  'What are your main skills?',
  'Tell me about your experience',
  'What certifications do you have?'
]

const askSuggested = (question: string) => {
  userInput.value = question
  sendMessage()
}

const sendMessage = async () => {
  const message = userInput.value.trim()
  if (!message || isLoading.value) return

  // Add user message
  messages.value.push({
    role: 'user',
    content: message
  })

  userInput.value = ''
  isLoading.value = true

  try {
    const { response, provider } = await aiService.sendMessage(message)

    if (!response || response.trim() === '') {
      messages.value.push({
        role: 'assistant',
        content: 'Sorry, I received an empty response. Please try again.'
      })
    } else {
      messages.value.push({
        role: 'assistant',
        content: response,
        provider
      })
    }
  } catch (error) {
    console.error('Error sending message:', error)
    messages.value.push({
      role: 'assistant',
      content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`
    })
  } finally {
    isLoading.value = false
  }
}

const resetChat = () => {
  messages.value = []
  userInput.value = ''
  aiService.clearCache()
}
</script>

<template>
  <div class="chatgpt-query print:hidden">
    <!-- Chat Button -->
    <button
      @click="openChat"
      class="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-white/5 px-4 py-2.5 font-semibold text-white transition-colors duration-200 hover:border-slate-400 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-300"
      :disabled="isLoading"
    >
      <ChatBubbleLeftRightIcon class="w-4 h-4" />
      <span class="hidden sm:inline">Ask about my experience</span>
      <span class="sm:hidden">Chat</span>
    </button>

    <!-- Chat Modal -->
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4"
      @click="closeChat"
      aria-hidden="true"
    >
      <div
        ref="modalRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-modal-title"
        class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[90vw] sm:w-[400px] md:w-[500px] lg:w-[600px] max-h-[80vh] flex flex-col mt-8"
        @click.stop
      >
        <!-- Modal Header -->
        <div
          class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700"
        >
          <h3 id="chat-modal-title" class="text-lg font-semibold text-gray-900 dark:text-white">
            Ask about my experience
          </h3>
          <button
            @click="closeChat"
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close chat"
          >
            <XMarkIcon class="w-6 h-6" aria-hidden="true" />
          </button>
        </div>

        <!-- Messages Area -->
        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-4 max-h-96">
          <!-- Welcome state when no messages -->
          <div v-if="messages.length === 0 && !isLoading" class="text-center py-6">
            <ChatBubbleLeftRightIcon class="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p class="text-gray-500 dark:text-gray-400 text-sm mb-4">
              Ask me anything about Jonathan's work experience, skills, or projects.
            </p>
            <div class="flex flex-wrap justify-center gap-2">
              <button
                v-for="question in suggestedQuestions"
                :key="question"
                @click="askSuggested(question)"
                class="text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {{ question }}
              </button>
            </div>
          </div>

          <div
            v-for="(message, index) in messages"
            :key="index"
            class="flex"
            :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <div
              class="max-w-[80%] rounded-lg px-4 py-2"
              :class="
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
              "
            >
              <div
                v-if="message.role === 'assistant'"
                class="prose-chat"
                v-html="renderMarkdown(message.content)"
              ></div>
              <p v-else class="whitespace-pre-wrap !mb-0 !text-left">{{ message.content }}</p>
              <span
                v-if="message.role === 'assistant' && message.provider"
                class="text-[10px] opacity-50 mt-1 block"
              >
                via <a v-if="message.provider === 'aifly'" href="https://www.aifly.click" target="_blank" rel="noopener" class="underline hover:opacity-80">aifly.click</a><template v-else-if="message.provider === 'chatgpt'">ChatGPT</template><template v-else>live CV</template>
              </span>
            </div>
          </div>

          <!-- Loading indicator -->
          <div v-if="isLoading" class="flex justify-start">
            <div class="bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-2">
              <div class="flex items-center space-x-2">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span class="text-gray-600 dark:text-gray-300">Thinking...</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Input Area (always visible) -->
        <div class="p-4 border-t border-gray-200 dark:border-gray-700">
          <form @submit.prevent="sendMessage" class="flex gap-2">
            <input
              v-model="userInput"
              type="text"
              placeholder="Ask about my work experience, skills, or projects..."
              class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
              :disabled="isLoading"
            />
            <button
              type="submit"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              :disabled="isLoading || !userInput.trim()"
            >
              <PaperAirplaneIcon class="w-4 h-4" />
            </button>
          </form>
          <button
            v-if="messages.length > 0 && !isLoading"
            @click="resetChat"
            class="w-full mt-2 px-4 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            Clear conversation
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar for messages */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Markdown rendering inside chat bubbles */
.prose-chat :deep(p) {
  margin: 0 0 0.5em 0;
  text-align: left;
}

.prose-chat :deep(p:last-child) {
  margin-bottom: 0;
}

.prose-chat :deep(ul),
.prose-chat :deep(ol) {
  margin: 0.25em 0;
  padding-left: 1.25em;
}

.prose-chat :deep(ul) {
  list-style-type: disc;
}

.prose-chat :deep(ol) {
  list-style-type: decimal;
}

.prose-chat :deep(li) {
  margin: 0.1em 0;
}

.prose-chat :deep(strong) {
  font-weight: 600;
}

.prose-chat :deep(code) {
  font-size: 0.85em;
  padding: 0.1em 0.3em;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.08);
}

.prose-chat :deep(pre) {
  margin: 0.5em 0;
  padding: 0.5em;
  border-radius: 4px;
  overflow-x: auto;
  background: rgba(0, 0, 0, 0.08);
}

.prose-chat :deep(pre code) {
  padding: 0;
  background: none;
}

.prose-chat :deep(a) {
  text-decoration: underline;
}

.prose-chat :deep(h1),
.prose-chat :deep(h2),
.prose-chat :deep(h3),
.prose-chat :deep(h4) {
  font-weight: 600;
  margin: 0.5em 0 0.25em 0;
}

.prose-chat :deep(blockquote) {
  border-left: 3px solid currentColor;
  opacity: 0.8;
  margin: 0.5em 0;
  padding-left: 0.75em;
}
</style>
